import _toConsumableArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _defineProperty from "/usr/app/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
var ScreenNavigationContext = /*#__PURE__*/createContext(undefined);
export function useScreenNavigation() {
  var context = useContext(ScreenNavigationContext);
  if (!context) {
    throw new Error("useScreenNavigation must be used within ScreenNavigationProvider");
  }
  return context;
}
export function ScreenNavigationProvider(_ref) {
  var children = _ref.children,
    initialScreen = _ref.initialScreen,
    _ref$baseKey = _ref.baseKey,
    baseKey = _ref$baseKey === void 0 ? "screen-navigation" : _ref$baseKey,
    _ref$syncWithURL = _ref.syncWithURL,
    syncWithURL = _ref$syncWithURL === void 0 ? false : _ref$syncWithURL,
    _ref$urlParamName = _ref.urlParamName,
    urlParamName = _ref$urlParamName === void 0 ? "screen" : _ref$urlParamName;
  // Read initial screen from URL if syncWithURL is enabled
  var getInitialScreenFromURL = function getInitialScreenFromURL() {
    if (!syncWithURL || !__IS_CSR__) return initialScreen;
    var params = new URLSearchParams(window.location.search);
    var screenFromURL = params.get(urlParamName);
    return screenFromURL || initialScreen;
  };
  var _useState = useState(getInitialScreenFromURL()),
    currentScreen = _useState[0],
    setCurrentScreen = _useState[1];
  var _useState2 = useState(null),
    previousScreen = _useState2[0],
    setPreviousScreen = _useState2[1];
  var _useState3 = useState([getInitialScreenFromURL()]),
    navigationHistory = _useState3[0],
    setNavigationHistory = _useState3[1];
  var _useState4 = useState(false),
    isNavigating = _useState4[0],
    setIsNavigating = _useState4[1];
  var _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 1),
    searchParams = _useSearchParams2[0];
  var navigationGuardRef = useRef(null);
  var isHandlingPopStateRef = useRef(false);
  var onNavigateBackRef = useRef(null);

  // Initialize browser history state on mount
  useEffect(function () {
    if (__IS_CSR__) {
      var currentState = window.history.state;
      var screenState = _defineProperty({}, baseKey, {
        screen: initialScreen,
        timestamp: Date.now()
      });

      // Replace initial state with our screen state
      window.history.replaceState(_objectSpread(_objectSpread({}, currentState), screenState), "", window.location.href);
    }
  }, [baseKey, initialScreen]);

  // Handle browser back/forward navigation
  useEffect(function () {
    if (!__IS_CSR__) return;
    var handlePopState = function handlePopState(event) {
      // Prevent recursive handling
      if (isHandlingPopStateRef.current) return;
      isHandlingPopStateRef.current = true;
      var state = event.state;
      var screenData = state === null || state === void 0 ? void 0 : state[baseKey];
      if (screenData !== null && screenData !== void 0 && screenData.screen) {
        // Check navigation guard
        if (navigationGuardRef.current && !navigationGuardRef.current.canNavigate()) {
          // Block navigation - push current screen back to history
          var currentState = _defineProperty({}, baseKey, {
            screen: currentScreen,
            timestamp: Date.now()
          });
          window.history.pushState(currentState, "", window.location.href);
          if (navigationGuardRef.current.onBlock) {
            navigationGuardRef.current.onBlock();
          }
          isHandlingPopStateRef.current = false;
          return;
        }

        // Detect if this is a forward navigation (screen not in current history)
        var isForwardNavigation = !navigationHistory.includes(screenData.screen);

        // If user pressed forward, prevent it by going back
        if (isForwardNavigation) {
          console.warn('Forward navigation is not allowed. Going back.');
          window.history.back();
          isHandlingPopStateRef.current = false;
          return;
        }

        // Allow navigation
        setIsNavigating(true);
        setPreviousScreen(currentScreen);
        setCurrentScreen(screenData.screen);

        // Update history array - trim to the target screen to remove forward history
        setNavigationHistory(function (prev) {
          var screenIndex = prev.lastIndexOf(screenData.screen);
          if (screenIndex !== -1) {
            // Trim history to remove everything after the target screen
            return prev.slice(0, screenIndex + 1);
          }
          // If screen not found in history (shouldn't happen), keep current history
          return prev;
        });

        // Call the navigate back callback if set
        if (onNavigateBackRef.current) {
          onNavigateBackRef.current(screenData.screen);
        }
        setIsNavigating(false);
      }
      isHandlingPopStateRef.current = false;
    };
    window.addEventListener("popstate", handlePopState);
    return function () {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [baseKey, currentScreen, navigationHistory]);
  var navigateToScreen = useCallback(function (screenName) {
    var replaceHistory = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    // Check navigation guard
    if (navigationGuardRef.current && !navigationGuardRef.current.canNavigate()) {
      if (navigationGuardRef.current.onBlock) {
        navigationGuardRef.current.onBlock();
      }
      return;
    }
    setIsNavigating(true);
    setPreviousScreen(currentScreen);
    setCurrentScreen(screenName);

    // Update navigation history
    setNavigationHistory(function (prev) {
      if (replaceHistory) {
        return [].concat(_toConsumableArray(prev.slice(0, -1)), [screenName]);
      }
      return [].concat(_toConsumableArray(prev), [screenName]);
    });

    // Update browser history
    if (__IS_CSR__) {
      var screenState = _defineProperty({}, baseKey, {
        previousScreen: previousScreen,
        screen: screenName,
        timestamp: Date.now()
      });

      // Update URL if syncWithURL is enabled
      var newURL = window.location.href;
      if (syncWithURL) {
        var url = new URL(window.location.href);
        url.searchParams.set(urlParamName, screenName);
        newURL = url.toString();
      }
      if (replaceHistory) {
        window.history.replaceState(screenState, "", newURL);
      } else {
        window.history.pushState(screenState, "", newURL);
      }
    }
    setIsNavigating(false);
  }, [baseKey, currentScreen, syncWithURL, urlParamName]);
  var navigateToPreviousScreen = useCallback(function (screenName) {
    var clearForwardHistory = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    // Check navigation guard
    if (navigationGuardRef.current && !navigationGuardRef.current.canNavigate()) {
      if (navigationGuardRef.current.onBlock) {
        navigationGuardRef.current.onBlock();
      }
      return;
    }
    setIsNavigating(true);
    setPreviousScreen(currentScreen);
    setCurrentScreen(screenName);

    // Clear forward history by trimming navigation history at the target screen
    if (clearForwardHistory) {
      setNavigationHistory(function (prev) {
        var screenIndex = prev.lastIndexOf(screenName);
        if (screenIndex !== -1) {
          // Keep history up to and including the target screen
          return prev.slice(0, screenIndex + 1);
        }
        // If screen not found, add it to history
        return [].concat(_toConsumableArray(prev), [screenName]);
      });
    }

    // Update browser history - REPLACE to prevent forward navigation
    if (__IS_CSR__) {
      var screenState = _defineProperty({}, baseKey, {
        screen: screenName,
        timestamp: Date.now()
      });

      // Replace current state to prevent forward navigation
      window.history.replaceState(screenState, "", window.location.href);
    }
    setIsNavigating(false);
  }, [baseKey, currentScreen]);
  var goBack = useCallback(function () {
    if (__IS_CSR__ && navigationHistory.length > 1) {
      // Check navigation guard
      if (navigationGuardRef.current && !navigationGuardRef.current.canNavigate()) {
        if (navigationGuardRef.current.onBlock) {
          navigationGuardRef.current.onBlock();
        }
        return;
      }

      // The actual navigation will be handled by the popstate event handler
      // which will update currentScreen, previousScreen, and trim the history
      window.history.back();
    }
  }, [navigationHistory.length]);
  var setNavigationGuard = useCallback(function (guard) {
    navigationGuardRef.current = guard;
  }, []);
  var clearHistory = useCallback(function () {
    setNavigationHistory([currentScreen]);
    setPreviousScreen(null);
  }, [currentScreen]);
  var canGoBack = useCallback(function () {
    return navigationHistory.length > 1;
  }, [navigationHistory.length, searchParams]);
  var setOnNavigateBack = useCallback(function (callback) {
    onNavigateBackRef.current = callback;
  }, []);
  var state = {
    currentScreen: currentScreen,
    previousScreen: previousScreen,
    navigationHistory: navigationHistory,
    isNavigating: isNavigating
  };
  var actions = {
    navigateToScreen: navigateToScreen,
    navigateToPreviousScreen: navigateToPreviousScreen,
    goBack: goBack,
    setNavigationGuard: setNavigationGuard,
    clearHistory: clearHistory,
    canGoBack: canGoBack,
    setOnNavigateBack: setOnNavigateBack
  };
  return /*#__PURE__*/React.createElement(ScreenNavigationContext.Provider, {
    value: {
      state: state,
      actions: actions
    }
  }, children);
}